// client/src/views/MapView.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Sidebar from './Sidebar';
import Comments from '../components/Comments';
import InteractiveMap from './InteractiveMap';
import SaveLayerModal from '../components/SaveLayerModal';
import IconUpload from '../components/IconUpload';
import logo from '../assets/logo128x128.png';
import 'leaflet/dist/leaflet.css';
import '../components/leaflet-geoman.css';
import './MapView.css';
import useStyles from '../components/styles/useStyles';
import Styler from '../components/styles/Styler';
import MapPrivacyControls from '../components/MapPrivacyControls';

const IS_DEV = process.env.NODE_ENV === 'development';
const API_BASE = '';
const VOX_PROMO_SEEN_KEY = 'pa_vox_promo_seen_v1';
const VOX_PROMO_DISMISSED_KEY = 'pa_vox_promo_dismissed_v1';

function apiUrl(path) {
  if (!path.startsWith('/')) return `${API_BASE}/${path}`;
  return `${API_BASE}${path}`;
}

function apiFetch(path, options) {
  if (typeof path === 'string' && /^\/api\/games\/\d+/.test(path)) {
    const body = JSON.stringify({});
    try {
      return Promise.resolve(
        new Response(body, {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        })
      );
    } catch {
      return Promise.resolve({
        ok: true,
        status: 200,
        json: async () => ({}),
        text: async () => body,
      });
    }
  }

  return fetch(apiUrl(path), options);
}

function useMobile(threshold = 800) {
  const [mobile, setMobile] = useState(window.innerWidth <= threshold);
  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth <= threshold);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [threshold]);
  return mobile;
}

function resolveUploadsUrl(src) {
  if (!src) return '';
  const trimmed = String(src).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (IS_DEV) {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `https://beta.point-atlas.io${path}`;
  }
  if (trimmed.startsWith('/')) return trimmed;
  return `/${trimmed}`;
}

function slugifyName(name) {
  const raw = name == null ? '' : String(name);
  const trimmed = raw.trim();
  const base = trimmed.length ? trimmed : 'Map';
  const s = base
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s/g, '-')
    .replace(/[^\w\-]+/g, '-')
    .replace(/\-+/g, '-')
    .replace(/^\-+|\-+$/g, '');
  return s || 'Map';
}

function getOrCreateSessionId() {
  if (typeof window === 'undefined') return '';
  const KEY = 'pa_session_id';
  try {
    let v = window.localStorage.getItem(KEY);
    if (v && typeof v === 'string' && v.trim() !== '') return v;
    const newId = Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.localStorage.setItem(KEY, newId);
    return newId;
  } catch {
    return '';
  }
}

function trackMapView(mapId) {
  if (!mapId) return;
  if (typeof window === 'undefined') return;
  const sessionId = getOrCreateSessionId();
  if (!sessionId) return;

  const body = JSON.stringify({
    mapId: Number(mapId),
    sessionId,
  });

  const send = () => {
    fetch('/api/metrics/map-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
    }).catch(() => {});
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(send, { timeout: 3000 });
  } else {
    setTimeout(send, 250);
  }
}

function MapView() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const rawParamId = params.id || params.mapId || null;
  const isLegacyUrl =
    typeof rawParamId === 'string' &&
    rawParamId.trim() !== '' &&
    !/^\d+$/.test(rawParamId.trim()) &&
    /(\d+)$/.test(rawParamId.trim());

  let mapId = rawParamId;
  if (mapId && typeof mapId === 'string') {
    const match = mapId.match(/(\d+)$/);
    if (match) mapId = match[1];
  }

  const expansionKey = mapId ? `sidebarExpansion_${mapId}` : null;

  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const [mapData, setMapData] = useState(null);
  const [parentMapData, setParentMapData] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [pois, setPois] = useState([]);
  const [pendingPOI, setPendingPOI] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [shape, setShape] = useState('circle');
  const [customCategories, setCustomCategories] = useState([]);
  const [customTypes, setCustomTypes] = useState({});
  const [typeColours, setTypeColours] = useState({});
  const [typeColourInput, setTypeColourInput] = useState('#ff0000');
  const [visibleTypes, setVisibleTypes] = useState(new Set());
  const [visibleCategories, setVisibleCategories] = useState(new Set());
  const [sidebarWidth, setSidebarWidth] = useState(300);

  const [hoveredType, setHoveredType] = useState(null);
  const [hoveredPoiId, setHoveredPoiId] = useState(null);

  const hoveredTypeRafRef = useRef(null);
  const hoveredPoiRafRef = useRef(null);
  const lastHoveredTypeRef = useRef(null);
  const lastHoveredPoiIdRef = useRef(null);

  const hoveredTypeClearTimeoutRef = useRef(null);
  const hoveredPoiClearTimeoutRef = useRef(null);

  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [voteStatus, setVoteStatus] = useState({ total_votes: 0, user_vote: 0 });
  const [isFavourited, setIsFavourited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const sidebarRef = useRef(null);

  const [leafletMap, setLeafletMap] = useState(null);
  const [user, setUser] = useState(null);

  const [rememberMapPrefs, setRememberMapPrefs] = useState(true);
  const [rememberResolved, setRememberResolved] = useState(false);

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsWidth, setCommentsWidth] = useState(420);
  const commentsPanelRef = useRef();

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [mouseCoord, setMouseCoord] = useState(null);
  const [shareCoord, setShareCoord] = useState(null);
  const [copiedCoord, setCopiedCoord] = useState(false);

  const [showAllPoiLabels, setShowAllPoiLabels] = useState(true);

  const [foundPOIs, setFoundPOIs] = useState([]);
  const [showFound, setShowFound] = useState(false);

  const mainMapRef = useRef();

  const [poiName, setPoiName] = useState('');

  const isMobile = useMobile(800);
  const [mobileDrawer, setMobileDrawer] = useState(null);

  const [flashType, setFlashType] = useState(null);

  const [activeTool, setActiveTool] = useState(null);
  const [pencilSize, setPencilSize] = useState(4);
  const [fill, setFill] = useState('#d43a3a');
  const [opacity, setOpacity] = useState(0.7);
  const [pattern, setPattern] = useState('none');

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const [showSaveLayerModal, setShowSaveLayerModal] = useState(false);
  const [pendingLayer, setPendingLayer] = useState(null);

  const [zones, setZones] = useState([]);
  const [visibleZones, setVisibleZones] = useState(new Set());
  const [zonesExpanded, setZonesExpanded] = useState(true);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);
  const [hideSidebarHover, setHideSidebarHover] = useState(false);

  const [extraRegionMaps, setExtraRegionMaps] = useState([]);

  const [communityMapId, setCommunityMapId] = useState(null);
  const [communityMapData, setCommunityMapData] = useState(null);

  const [mapKey, setMapKey] = useState(mapId);

  const [isPublic, setIsPublic] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pubStatusMsg, setPubStatusMsg] = useState('');

  const [stylerOpen, setStylerOpen] = useState(false);

  const [pathControls, setPathControls] = useState({});

  const [seoExtra, setSeoExtra] = useState('');
  const [seoApproved, setSeoApproved] = useState(false);
  const [seoUpdating, setSeoUpdating] = useState(false);
  const [seoStatusMsg, setSeoStatusMsg] = useState('');

  const [activeOverlayId, setActiveOverlayId] = useState(null);
  const [hasActiveOverlay, setHasActiveOverlay] = useState(false);

  const [overlayAssets, setOverlayAssets] = useState([]);
  const [overlayAssetsLoading, setOverlayAssetsLoading] = useState(false);

  const [showVoxPromo, setShowVoxPromo] = useState(false);

  const hasTrackedViewRef = useRef(false);

  const {
    styles: mapStyles,
    loading: stylesLoading,
    error: stylesError,
    saveStyles,
    resetStyles,
    setDefault,
    refetch: refetchStyles,
    defaultStyles
  } = useStyles(mapId, user);

  const openPrivacyCookieSettings = useCallback(() => {
    try {
      if (typeof window === 'undefined') return;

      const w = window;

      if (typeof w.__cmp === 'function') {
        try {
          w.__cmp('showModal');
          return;
        } catch {}
      }

      if (typeof w.__tcfapi === 'function') {
        try {
          w.__tcfapi('displayConsentUi', 2, () => {});
          return;
        } catch {}
      }

      try {
        w.open('/privacy', '_blank', 'noopener,noreferrer');
      } catch {
        try {
          w.location.href = '/privacy';
        } catch {}
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.__paOpenPrivacyCookieSettings = openPrivacyCookieSettings;
    } catch {}
    return () => {
      try {
        if (typeof window === 'undefined') return;
        if (window.__paOpenPrivacyCookieSettings === openPrivacyCookieSettings) {
          delete window.__paOpenPrivacyCookieSettings;
        }
      } catch {}
    };
  }, [openPrivacyCookieSettings]);

  const openStylerForOwner = useCallback(() => {
    try {
      setStylerOpen(true);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;

      const viewerId = user?.id != null ? Number(user.id) : null;
      const ownerId = mapData?.uploader_id != null ? Number(mapData.uploader_id) : null;
      const isOwnerOnly = !!viewerId && ownerId != null && viewerId === ownerId;

      if (isOwnerOnly) {
        window.__paOpenStyler = openStylerForOwner;
      } else {
        if (window.__paOpenStyler === openStylerForOwner) {
          delete window.__paOpenStyler;
        } else if (typeof window.__paOpenStyler === 'function') {
          delete window.__paOpenStyler;
        }
      }
    } catch {}

    return () => {
      try {
        if (typeof window === 'undefined') return;
        if (window.__paOpenStyler === openStylerForOwner) {
          delete window.__paOpenStyler;
        }
      } catch {}
    };
  }, [user, mapData, openStylerForOwner]);

  const setHoveredTypeSmooth = useCallback((nextType) => {
    if (hoveredTypeClearTimeoutRef.current) {
      clearTimeout(hoveredTypeClearTimeoutRef.current);
      hoveredTypeClearTimeoutRef.current = null;
    }

    if (hoveredTypeRafRef.current) cancelAnimationFrame(hoveredTypeRafRef.current);

    if (nextType == null) {
      hoveredTypeClearTimeoutRef.current = setTimeout(() => {
        hoveredTypeClearTimeoutRef.current = null;
        if (lastHoveredTypeRef.current === null) return;
        lastHoveredTypeRef.current = null;
        setHoveredType(null);
      }, 60);
      return;
    }

    hoveredTypeRafRef.current = requestAnimationFrame(() => {
      hoveredTypeRafRef.current = null;
      if (lastHoveredTypeRef.current === nextType) return;
      lastHoveredTypeRef.current = nextType;
      setHoveredType(nextType);
    });
  }, []);

  const setHoveredPoiIdSmooth = useCallback((nextPoiId) => {
    if (hoveredPoiClearTimeoutRef.current) {
      clearTimeout(hoveredPoiClearTimeoutRef.current);
      hoveredPoiClearTimeoutRef.current = null;
    }

    if (hoveredPoiRafRef.current) cancelAnimationFrame(hoveredPoiRafRef.current);

    if (nextPoiId == null) {
      hoveredPoiClearTimeoutRef.current = setTimeout(() => {
        hoveredPoiClearTimeoutRef.current = null;
        if (lastHoveredPoiIdRef.current === null) return;
        lastHoveredPoiIdRef.current = null;
        setHoveredPoiId(null);
      }, 60);
      return;
    }

    hoveredPoiRafRef.current = requestAnimationFrame(() => {
      hoveredPoiRafRef.current = null;
      if (lastHoveredPoiIdRef.current === nextPoiId) return;
      lastHoveredPoiIdRef.current = nextPoiId;
      setHoveredPoiId(nextPoiId);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (hoveredTypeRafRef.current) cancelAnimationFrame(hoveredTypeRafRef.current);
      if (hoveredPoiRafRef.current) cancelAnimationFrame(hoveredPoiRafRef.current);

      if (hoveredTypeClearTimeoutRef.current) clearTimeout(hoveredTypeClearTimeoutRef.current);
      if (hoveredPoiClearTimeoutRef.current) clearTimeout(hoveredPoiClearTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setMapKey(mapId);
  }, [mapId]);

  useEffect(() => {
    apiFetch('/api/me', { credentials: 'include' })
      .then(res => res.json())
      .then(u => setUser(u))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    let ignore = false;

    (async () => {
      if (!user || !user.id) {
        if (!ignore) {
          setRememberMapPrefs(true);
          setRememberResolved(true);
        }
        return;
      }

      try {
        const res = await apiFetch('/api/user/settings', { credentials: 'include' });
        if (ignore) return;

        if (res.status === 401) {
          setRememberMapPrefs(true);
          setRememberResolved(true);
          return;
        }

        if (!res.ok) {
          setRememberMapPrefs(true);
          setRememberResolved(true);
          return;
        }

        const data = await res.json();
        const v = data && data.remember_map_prefs != null ? Number(data.remember_map_prefs) : 1;
        setRememberMapPrefs(v === 1);
        setRememberResolved(true);
      } catch {
        if (!ignore) {
          setRememberMapPrefs(true);
          setRememberResolved(true);
        }
      }
    })();

    return () => { ignore = true; };
  }, [user]);

  useEffect(() => {
    if (!rememberResolved) return;

    if (rememberMapPrefs) return;

    try {
      const keys = Object.keys(window.localStorage || {});
      keys.forEach((k) => {
        if (!k) return;
        if (k === 'showAllPoiLabels') window.localStorage.removeItem(k);
        if (mapId && k === `sidebarExpansion_${mapId}`) window.localStorage.removeItem(k);
        if (mapId && k === `foundPois_${mapId}`) window.localStorage.removeItem(k);
        if (mapId && k === `pathSection_${mapId}`) window.localStorage.removeItem(k);
        if (mapId && k === `zoneSection_${mapId}`) window.localStorage.removeItem(k);
        if (mapId && k === `poiLabelScale_${mapId}`) window.localStorage.removeItem(k);
      });
    } catch {}

    setShowAllPoiLabels(true);
    setFoundPOIs([]);
  }, [rememberMapPrefs, rememberResolved, mapId]);

  useEffect(() => {
    if (!mapId) {
      setFoundPOIs([]);
      return;
    }

    if (!rememberResolved) return;

    if (!rememberMapPrefs) {
      setFoundPOIs([]);
      return;
    }

    try {
      const key = `foundPois_${mapId}`;
      const val = localStorage.getItem(key);
      if (val) setFoundPOIs(JSON.parse(val));
      else setFoundPOIs([]);
    } catch {
      setFoundPOIs([]);
    }
  }, [mapId, rememberMapPrefs, rememberResolved]);

  useEffect(() => {
    if (!rememberResolved) return;

    if (!rememberMapPrefs) {
      setShowAllPoiLabels(true);
      return;
    }

    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('showAllPoiLabels');
        if (stored != null) setShowAllPoiLabels(stored === 'true');
      } catch {}
    }
  }, [rememberMapPrefs, rememberResolved]);

  useEffect(() => {
    if (!rememberResolved) return;
    if (!rememberMapPrefs) return;

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('showAllPoiLabels', showAllPoiLabels ? 'true' : 'false');
      } catch {}
    }
  }, [showAllPoiLabels, rememberMapPrefs, rememberResolved]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (status !== 'ready') return;

    try {
      const host = String(window.location.hostname || '').toLowerCase();
      if (host.includes('voxemera.com')) return;

      const dismissed = window.localStorage.getItem(VOX_PROMO_DISMISSED_KEY) === '1';
      const seen = window.localStorage.getItem(VOX_PROMO_SEEN_KEY) === '1';

      if (dismissed || seen) {
        setShowVoxPromo(false);
        return;
      }

      window.localStorage.setItem(VOX_PROMO_SEEN_KEY, '1');
      setShowVoxPromo(true);
    } catch {
      setShowVoxPromo(false);
    }
  }, [status]);

  const dismissVoxPromo = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(VOX_PROMO_DISMISSED_KEY, '1');
      }
    } catch {}
    setShowVoxPromo(false);
  }, []);

  const saveFoundPOIs = (ids) => {
    setFoundPOIs(ids);

    if (!rememberResolved) return;
    if (!rememberMapPrefs) return;

    const key = `foundPois_${mapId}`;
    try {
      localStorage.setItem(key, JSON.stringify(ids));
    } catch {}
  };

  const togglePOIFound = (poiId) => {
    let newFound;
    if (foundPOIs.includes(poiId)) newFound = foundPOIs.filter(id => id !== poiId);
    else newFound = [...foundPOIs, poiId];
    saveFoundPOIs(newFound);
  };

  const loadExpansionState = () => {
    if (!rememberResolved) return null;
    if (!rememberMapPrefs) return null;
    if (!expansionKey) return null;
    try {
      const raw = localStorage.getItem(expansionKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const saveExpansionState = (expandedCats, expandedTypesObj) => {
    if (!rememberResolved) return;
    if (!rememberMapPrefs) return;
    if (!expansionKey) return;
    try {
      localStorage.setItem(
        expansionKey,
        JSON.stringify({
          expandedCategories: expandedCats || {},
          expandedTypes: expandedTypesObj || {}
        })
      );
    } catch {}
  };

  async function fetchMapById(targetId) {
    if (!targetId) {
      const err = new Error('notfound');
      err.code = 'NOT_FOUND';
      throw err;
    }
    const res = await apiFetch(`/api/maps/${targetId}`, { credentials: 'include' });
    if (res.status === 404) {
      const err = new Error('notfound');
      err.code = 'NOT_FOUND';
      throw err;
    }
    if (!res.ok) {
      const err = new Error(`Server error: ${res.status}`);
      err.code = 'SERVER';
      throw err;
    }
    return res.json();
  }

  function loadPOIs() {
    apiFetch(`/api/poi/all/${mapId}`)
      .then(res => res.json())
      .then(data => {
        const pois = Array.isArray(data.pois) ? data.pois : [];
        setPois(pois);

        const cats = new Set();
        const typesByCat = {};
        const colours = {};

        for (const poi of pois) {
          cats.add(poi.category);
          if (!typesByCat[poi.category]) typesByCat[poi.category] = [];
          if (!typesByCat[poi.category].includes(poi.type)) {
            typesByCat[poi.category].push(poi.type);
            colours[poi.type] = `#${(poi.color || 0).toString(16).padStart(6, '0')}`;
          }
        }

        for (const cat in typesByCat) {
          typesByCat[cat].sort((a, b) => a.localeCompare(b));
        }

        const sortedCats = Array.from(cats).sort((a, b) => a.localeCompare(b));

        setCustomCategories(sortedCats);
        setCustomTypes(typesByCat);
        setTypeColours(colours);
        setVisibleCategories(new Set(sortedCats));
        setVisibleTypes(new Set(Object.values(typesByCat).flat()));

        const saved = loadExpansionState();
        let nextExpandedCategories = {};
        let nextExpandedTypes = {};

        if (saved && saved.expandedCategories && typeof saved.expandedCategories === 'object') {
          sortedCats.forEach(cat => {
            nextExpandedCategories[cat] =
              Object.prototype.hasOwnProperty.call(saved.expandedCategories, cat)
                ? !!saved.expandedCategories[cat]
                : true;
          });
        } else {
          sortedCats.forEach(cat => { nextExpandedCategories[cat] = true; });
        }

        if (saved && saved.expandedTypes && typeof saved.expandedTypes === 'object') {
          nextExpandedTypes = saved.expandedTypes;
        } else {
          nextExpandedTypes = {};
        }

        setExpandedCategories(nextExpandedCategories);
        setExpandedTypes(nextExpandedTypes);
        saveExpansionState(nextExpandedCategories, nextExpandedTypes);
      });
  }

  function loadZones() {
    if (!mapId) return;
    apiFetch(`/api/polygons?mapId=${mapId}`)
      .then(res => res.json())
      .then(data => {
        setZones(data);
        const allZoneIds = new Set(data.map(zone => zone.id));
        setVisibleZones(allZoneIds);
      })
      .catch(() => {
        setZones([]);
        setVisibleZones(new Set());
      });
  }

  const loadOverlayAssetsForMap = useCallback(async () => {
    if (!mapId) {
      setOverlayAssets([]);
      return;
    }

    setOverlayAssetsLoading(true);
    try {
      const res = await fetch(`/api/tabletop/maps/${mapId}/assets`, { credentials: 'include' });
      const data = await res.json().catch(() => null);
      if (!Array.isArray(data)) {
        setOverlayAssets([]);
        return;
      }

      const allAssetsWithUrl = data.filter((a) => {
        if (!a) return false;
        const url = a.url || a.image_url || a.file_path || a.path || a.filepath;
        return !!url;
      });

      setOverlayAssets(allAssetsWithUrl);
    } catch {
      setOverlayAssets([]);
    } finally {
      setOverlayAssetsLoading(false);
    }
  }, [mapId]);

  useEffect(() => {
    loadOverlayAssetsForMap();
  }, [loadOverlayAssetsForMap]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.mapId && mapId && String(detail.mapId) !== String(mapId)) return;
      loadOverlayAssetsForMap();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('tabletop-assets-updated', handler);
      return () => window.removeEventListener('tabletop-assets-updated', handler);
    }
    return undefined;
  }, [mapId, loadOverlayAssetsForMap]);

  const sendOverlaySelectEvent = useCallback((assetIdOrNone) => {
    try {
      if (typeof window === 'undefined') return;
      window.dispatchEvent(
        new CustomEvent('tabletop-overlay-select-asset', {
          detail: { assetId: assetIdOrNone }
        })
      );
    } catch {}
  }, []);

  const hasOverlayChoices = Array.isArray(overlayAssets) && overlayAssets.length > 0;

  const handleOverlayPopupPick = (val) => {
    const v = val || 'none';
    if (v === 'none') sendOverlaySelectEvent('none');
    else sendOverlaySelectEvent(v);
    setMobileDrawer(null);
  };

  useEffect(() => {
    if (!isMobile) return;
    if (mobileDrawer !== 'overlay') return;

    const onKey = (e) => {
      if (e.key === 'Escape') setMobileDrawer(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, mobileDrawer]);

  useEffect(() => {
    let aborted = false;
    if (!mapId) {
      setStatus('notfound');
      setMapData(null);
      setParentMapData(null);
      setCommunityMapData(null);
      setCommunityMapId(null);
      setGameData(null);
      setImageSize(null);
      return;
    }

    hasTrackedViewRef.current = false;

    setStatus('loading');
    setErrorMsg('');
    setMapData(null);
    setParentMapData(null);
    setCommunityMapData(null);
    setCommunityMapId(null);
    setGameData(null);
    setImageSize(null);

    (async () => {
      try {
        const data = await fetchMapById(mapId);
        if (aborted) return;

        if (!data || (!data.image_url && !data.image && !data.tile_url)) {
          setStatus('notfound');
          return;
        }

        let nextParent = null;
        let nextCommunity = null;

        if (data.parent_map_id) {
          try {
            const parent = await fetchMapById(data.parent_map_id);
            if (aborted) return;
            nextParent = parent;
            nextCommunity = parent;
          } catch {
            nextParent = null;
            nextCommunity = data;
          }
        } else {
          nextParent = null;
          nextCommunity = data;
        }

        const viewerId = user?.id ? Number(user.id) : null;
        const ownerIdForThisPage = data?.uploader_id != null ? Number(data.uploader_id) : null;
        const isFounderLocal = !!viewerId && viewerId === 1;
        const isOwner = !!viewerId && ownerIdForThisPage != null && viewerId === ownerIdForThisPage;
        const hasPoiEditorAccess = !!data?.poi_editor_access;

        const pageIsPublic = !!data.is_public;

        if (!pageIsPublic && !isOwner && !isFounderLocal && !hasPoiEditorAccess) {
          setStatus('notfound');
          return;
        }

        setParentMapData(nextParent);
        setCommunityMapId(nextCommunity.id);
        setCommunityMapData(nextCommunity);
        setExtraRegionMaps(nextCommunity.extra_region_maps || []);

        setIsPublic(!!data.is_public);

        setMapData(data);
        setSeoExtra(typeof data.seo_extra === 'string' ? data.seo_extra : '');
        setSeoApproved(!!data.seo_approved);

        const inferredGameObj =
          data && typeof data === 'object' && data.game && typeof data.game === 'object'
            ? data.game
            : null;

        const inferredGameName =
          typeof data?.game_name === 'string'
            ? data.game_name
            : typeof data?.gameName === 'string'
            ? data.gameName
            : typeof data?.game_title === 'string'
            ? data.game_title
            : typeof data?.gameTitle === 'string'
            ? data.gameTitle
            : null;

        if (inferredGameObj) setGameData(inferredGameObj);
        else if (inferredGameName) setGameData({ id: data?.game_id ?? null, name: inferredGameName });
        else setGameData(null);

        const heroUrl = resolveUploadsUrl(data.image_url || data.image || '');
        if (heroUrl) {
          try {
            const img = new window.Image();
            img.loading = 'eager';
            img.decoding = 'async';
            img.onload = () => {
              if (aborted) return;
              setImageSize([img.height, img.width]);
              setStatus('ready');
            };
            img.onerror = () => {
              if (aborted) return;
              setImageSize(null);
              setStatus('ready');
            };
            img.src = heroUrl;
            if (img.decode) img.decode().catch(() => {});
          } catch {
            setStatus('ready');
          }
        } else {
          setStatus('ready');
        }

        loadPOIs();
        loadZones();
      } catch (e) {
        if (!aborted) {
          if (e && e.code === 'NOT_FOUND') setStatus('notfound');
          else {
            setStatus('error');
            setErrorMsg(e?.message || 'Unknown error');
          }
        }
      }
    })();

    return () => { aborted = true; };
  }, [mapId, user, rememberMapPrefs, rememberResolved]);

  useEffect(() => {
    const viewedId = mapData && mapData.id ? Number(mapData.id) : null;
    if (!viewedId) return;
    if (status !== 'ready') return;
    if (hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    try {
      trackMapView(viewedId);
    } catch {}
  }, [mapData, status]);

  useEffect(() => {
    if (mapData) setIsPublic(!!mapData.is_public);
  }, [mapData]);

  useEffect(() => {
    if (!communityMapId) return;
    let ignore = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/map-votes/${communityMapId}/total`);
        const totalData = await res.json();
        if (!ignore) {
          setVoteStatus(prev => ({
            total_votes: Number(totalData?.total_votes ?? 0),
            user_vote: Number(prev?.user_vote ?? 0),
          }));
        }
      } catch {
        if (!ignore) {
          setVoteStatus(prev => ({ total_votes: 0, user_vote: Number(prev?.user_vote ?? 0) }));
        }
      }
    })();
    return () => { ignore = true; };
  }, [communityMapId]);

  useEffect(() => {
    if (!communityMapId) return;
    let ignore = false;
    (async () => {
      if (!user) {
        setVoteStatus(prev => ({ total_votes: Number(prev?.total_votes ?? 0), user_vote: 0 }));
        return;
      }
      try {
        const res = await apiFetch(`/api/map-votes/${communityMapId}/${user.id}`);
        const userData = await res.json();
        if (!ignore) {
          setVoteStatus(prev => ({
            total_votes: Number(prev?.total_votes ?? 0),
            user_vote: Number(userData?.user_vote ?? 0),
          }));
        }
      } catch {
        if (!ignore) {
          setVoteStatus(prev => ({ total_votes: 0, user_vote: 0 }));
        }
      }
    })();
    return () => { ignore = true; };
  }, [user, communityMapId]);

  useEffect(() => {
    if (!communityMapId || !user) {
      setIsFavourited(false);
      return;
    }
    apiFetch('/api/user/favourites', { credentials: 'include' })
      .then(res => res.json())
      .then(favs => {
        setIsFavourited(favs.some(fav => parseInt(fav.id) === parseInt(communityMapId)));
      })
      .catch(() => setIsFavourited(false));
  }, [user, communityMapId]);

  const refreshVotes = async () => {
    if (!communityMapId) return;
    try {
      const [totalRes, userRes] = await Promise.all([
        apiFetch(`/api/map-votes/${communityMapId}/total`),
        user ? apiFetch(`/api/map-votes/${communityMapId}/${user.id}`) : Promise.resolve(null)
      ]);
      const totalData = await totalRes.json();
      const userData = userRes ? await userRes.json() : null;
      setVoteStatus({
        total_votes: Number(totalData?.total_votes ?? 0),
        user_vote: Number(userData?.user_vote ?? 0)
      });
    } catch {
      setVoteStatus(prev => ({ total_votes: Number(prev?.total_votes ?? 0), user_vote: Number(prev?.user_vote ?? 0) }));
    }
  };

  const handleFavourite = async () => {
    if (!user) return alert('You must be logged in to favourite maps!');
    setFavLoading(true);
    try {
      if (isFavourited) {
        await apiFetch(`/api/user/favourites/${communityMapId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setIsFavourited(false);
      } else {
        await apiFetch(`/api/user/favourites/${communityMapId}`, {
          method: 'POST',
          credentials: 'include'
        });
        setIsFavourited(true);
      }
    } finally {
      setFavLoading(false);
    }
  };

  const handleVote = async (vote) => {
    if (!user) return alert('You must be logged in to vote!');
    await apiFetch(`/api/map-votes/${communityMapId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: user.id, vote }),
    });
    await refreshVotes();
  };

  const isMapOwnerOrFounder = !!user && (
    user.id === mapData?.uploader_id ||
    user.id === 1 ||
    !!mapData?.poi_editor_access
  );


  const isFounder = user && user.id === 1;

  const handleSeoEditClick = async () => {
    if (!isMapOwnerOrFounder || !mapData?.id) return;
    if (seoApproved && (!user || user.id !== 1)) {
      alert('SEO for this map is approved and locked.');
      return;
    }

    const current = seoExtra || '';
    const next = window.prompt(
      'Extra SEO text for this map (optional, appended to the meta description/keywords):',
      current
    );
    if (next === null) return;
    const trimmed = next.trim();

    try {
      const res = await apiFetch(`/api/maps/${mapData.id}/seo-extra`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ seo_extra: trimmed })
      });
      if (!res.ok) throw new Error('Failed to save SEO text');
      setSeoExtra(trimmed);
      setMapData(prev => (prev ? { ...prev, seo_extra: trimmed } : prev));
    } catch {
      alert('Error saving SEO text.');
    }
  };

  const handleMapCoordClick = (e) => {
    if (!isMapOwnerOrFounder && !pendingPOI) {
      setShareCoord(e);
      setCopiedCoord(false);
    }
  };

  const handleMouseCoord = (coord) => setMouseCoord(coord);

  const handleCopyCoord = (coord) => {
    if (coord) {
      const str = `X: ${Math.round(coord.x)}, Y: ${Math.round(coord.y)}`;
      navigator.clipboard.writeText(str);
      setCopiedCoord(true);
      setTimeout(() => setCopiedCoord(false), 1500);
    } else {
      setShareCoord(null);
      setCopiedCoord(false);
    }
  };

  const handleClick = (coords) => {
    setPendingPOI(coords);
    setSelectedCategory('');
    setSelectedType('');
    setDescription('');
    setTypeColourInput('#ff0000');
    setShape('circle');
    setPoiName('');
  };

  const savePOI = async (poiNameArg, imageUrlArg, descriptionArg, extraOrOverlay = {}) => {
    if (!selectedCategory || !selectedType || !pendingPOI) return;

    let extra = {};
    if (
      extraOrOverlay !== null &&
      extraOrOverlay !== undefined &&
      typeof extraOrOverlay === 'object' &&
      !Array.isArray(extraOrOverlay)
    ) {
      extra = extraOrOverlay;
    } else if (extraOrOverlay !== null && extraOrOverlay !== undefined && extraOrOverlay !== '') {
      extra = { overlay_id: extraOrOverlay };
    }

    const categoryRes = await apiFetch('/api/poi/category', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: selectedCategory, map_id: parseInt(mapId, 10), user_id: user?.id })
    });
    const { id: category_id } = await categoryRes.json();

    const typeRes = await apiFetch('/api/poi/type', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: selectedType,
        map_id: parseInt(mapId, 10),
        category_id,
        color: parseInt(typeColourInput.replace('#', ''), 16),
        icon: shape
      })
    });
    const { id: type_id } = await typeRes.json();

    const finalDescription = (descriptionArg ?? description ?? '').trim();

    const baseBody = {
      map_id: parseInt(mapId, 10),
      category_id,
      type_id,
      x: pendingPOI.x,
      y: pendingPOI.y,
      description: finalDescription,
      poi_name: poiNameArg ? poiNameArg.trim() : '',
      image_url: imageUrlArg || '',
      created_by: user?.id
    };

    const cleanedExtra = { ...extra };
    if (Object.prototype.hasOwnProperty.call(cleanedExtra, 'overlay_id')) {
      const raw = cleanedExtra.overlay_id;
      if (raw == null || raw === '') delete cleanedExtra.overlay_id;
      else if (typeof raw === 'object' && raw.id != null) cleanedExtra.overlay_id = Number(raw.id);
      else cleanedExtra.overlay_id = Number(raw);
    }

    const body = { ...baseBody, ...cleanedExtra };

    const res = await apiFetch('/api/poi/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    setPendingPOI(null);
    setPoiName('');
    loadPOIs();

    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  const handleCategorySaved = () => loadPOIs();
  const handleZonesChanged = () => loadZones();

  const handlePathControlChange = (zoneId, changes) => {
    setPathControls(prev => {
      const prevForZone = prev[zoneId] || {};
      return { ...prev, [zoneId]: { ...prevForZone, ...changes } };
    });
  };

  const toggleType = (type) => {
    setVisibleTypes(prev => {
      const newSet = new Set(prev);
      newSet.has(type) ? newSet.delete(type) : newSet.add(type);
      return newSet;
    });
  };

  const toggleCategory = (category) => {
    setVisibleCategories(prev => {
      const newSet = new Set(prev);
      newSet.has(category) ? newSet.delete(category) : newSet.add(category);
      return newSet;
    });
  };

  const toggleAllTypes = () => {
    const allTypes = Object.values(customTypes).flat();
    const allVisible = allTypes.every(type => visibleTypes.has(type));
    setVisibleTypes(allVisible ? new Set() : new Set(allTypes));
  };

  const toggleCategoryExpand = (category, e) => {
    e.stopPropagation();
    setExpandedCategories(prev => {
      const next = { ...prev, [category]: !prev[category] };
      saveExpansionState(next, expandedTypes);
      return next;
    });
  };

  const toggleZone = (zoneId) => {
    setVisibleZones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) newSet.delete(zoneId);
      else newSet.add(zoneId);
      return newSet;
    });
  };

  const toggleAllZones = () => {
    if (visibleZones.size === zones.length) setVisibleZones(new Set());
    else setVisibleZones(new Set(zones.map(zone => zone.id)));
  };

  const toggleZonesExpand = () => setZonesExpanded(prev => !prev);

  const handleSidebarMouseDown = (e) => {
    const startX = e.clientX;
    const startWidth = sidebarRef.current.offsetWidth;

    const onMouseMove = (eMove) => {
      const newWidth = startWidth + (eMove.clientX - startX);
      if (newWidth >= 150 && newWidth <= 600) setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleCommentsMouseDown = (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
    } catch {}

    const startX = e.clientX;
    const startWidth = commentsPanelRef.current ? commentsPanelRef.current.offsetWidth : commentsWidth;

    const onMouseMove = (eMove) => {
      const delta = startX - eMove.clientX;
      const newWidth = startWidth + delta;
      if (newWidth >= 260 && newWidth <= 760) setCommentsWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const allTypes = Object.values(customTypes).flat();
  const allTypesVisible = allTypes.every(type => visibleTypes.has(type));

  const poiLabelToggleBtn = (
    <button
      style={{
        margin: '10px 0 10px 0',
        background: showAllPoiLabels ? '#ffdc5e' : '#232323',
        color: showAllPoiLabels ? '#232' : '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '7px 16px',
        fontWeight: 700,
        fontSize: 16,
        cursor: 'pointer',
      }}
      onClick={() => setShowAllPoiLabels(v => !v)}
    >
      {showAllPoiLabels ? 'Hide all POI labels' : 'Show all POI labels'}
    </button>
  );

  const handleMobileTypeClick = (type) => {
    setMobileDrawer(null);
    setFlashType(type);
    setTimeout(() => setFlashType(null), 1500);
  };

  const handleRegionMapClick = (regionMap) => {
    const rid = regionMap && regionMap.id != null ? String(regionMap.id) : null;
    if (!rid) return;
    if (String(rid) === String(mapId)) return;

    const name = regionMap && regionMap.name ? regionMap.name : 'Map';
    const slug = slugifyName(name);

    const isVttRoute =
      (location && typeof location.pathname === 'string' && location.pathname.startsWith('/vtt/')) || false;
    const base = isVttRoute ? '/vtt' : '/map';

    navigate(`${base}/${encodeURIComponent(rid)}/${encodeURIComponent(slug)}`);
  };

  const handleAddNewRegionMap = async () => {
    if (!communityMapId) return;
    try {
      const data = await fetchMapById(communityMapId);
      setCommunityMapData(data);
      setExtraRegionMaps(data.extra_region_maps || []);
    } catch {}
  };

  const refetchCurrentMapState = useCallback(async () => {
    if (!mapData?.id) return;

    try {
      const fresh = await fetchMapById(mapData.id);
      setMapData(fresh);

      let nextCommunity = fresh;
      let nextParent = null;

      if (fresh.parent_map_id) {
        try {
          const parent = await fetchMapById(fresh.parent_map_id);
          nextParent = parent;
          nextCommunity = parent;
        } catch {}
      }

      setParentMapData(nextParent);
      setCommunityMapData(nextCommunity);
      setCommunityMapId(nextCommunity?.id || null);
      setExtraRegionMaps(nextCommunity?.extra_region_maps || []);
      setIsPublic(!!fresh.is_public);
      setSeoExtra(typeof fresh.seo_extra === 'string' ? fresh.seo_extra : '');
      setSeoApproved(!!fresh.seo_approved);
    } catch {}
  }, [mapData]);

  const renderMobileDrawer = () => {
    if (!mobileDrawer) return null;

    if (mobileDrawer === 'overlay') {
      return (
        <div
          onClick={() => setMobileDrawer(null)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 4005,
            background: 'transparent',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: 12,
              bottom: 72,
              width: Math.min(320, Math.max(240, (typeof window !== 'undefined' ? window.innerWidth : 320) - 24)),
              borderRadius: 12,
              background: 'rgba(15,18,30,0.97)',
              border: '1px solid rgba(80,90,130,0.9)',
              boxShadow: '0 10px 24px rgba(0,0,0,0.75)',
              color: '#f4f5ff',
              padding: 10,
              pointerEvents: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.2 }}>Overlay</div>
              <button
                type="button"
                onClick={() => setMobileDrawer(null)}
                style={{
                  marginLeft: 'auto',
                  padding: '2px 8px',
                  borderRadius: 8,
                  border: '1px solid rgba(80,90,130,0.9)',
                  background: '#111320',
                  color: '#f4f5ff',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: '18px',
                }}
              >
                ×
              </button>
            </div>

            {!hasOverlayChoices && !overlayAssetsLoading && (
              <div style={{ opacity: 0.9, fontSize: 13, lineHeight: 1.35, padding: '4px 2px' }}>
                No overlays found for this map.
              </div>
            )}

            {overlayAssetsLoading && (
              <div style={{ opacity: 0.9, fontSize: 13, lineHeight: 1.35, padding: '4px 2px' }}>
                Loading overlays…
              </div>
            )}

            {hasOverlayChoices && (
              <>
                <select
                  value={hasActiveOverlay && activeOverlayId != null ? String(activeOverlayId) : 'none'}
                  onChange={(e) => handleOverlayPopupPick(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid rgba(90,100,150,0.9)',
                    background: '#111320',
                    color: '#f4f5ff',
                    fontSize: 14,
                    outline: 'none',
                  }}
                >
                  {overlayAssets
                    .slice()
                    .reverse()
                    .map((a) => {
                      const label = a.title || a.name || 'Untitled overlay';
                      return (
                        <option key={a.id} value={String(a.id)}>
                          {label}
                        </option>
                      );
                    })}
                  <option value="none">Ground floor (no overlay)</option>
                </select>

                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => {
                      loadOverlayAssetsForMap();
                    }}
                    style={{
                      flex: '1 1 0%',
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1px solid rgba(80,90,130,0.9)',
                      background: '#1b1f2e',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOverlayPopupPick('none')}
                    style={{
                      flex: '1 1 0%',
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1px solid rgba(80,90,130,0.9)',
                      background: '#1b1f2e',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Clear
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mobile-drawer-overlay" onClick={() => setMobileDrawer(null)}>
        <div className="mobile-drawer" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
          <button
            className="sidebar-mobile-close"
            onClick={() => setMobileDrawer(null)}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 60,
              right: 17,
              width: 48,
              height: 48,
              background: '#212326',
              color: '#fff',
              fontSize: '2em',
              fontWeight: 900,
              zIndex: 4002,
              lineHeight: 1,
              borderRadius: '50%',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          {mobileDrawer === 'sidebar' && (
            <Sidebar
              key={communityMapData?.id}
              sidebarWidth={320}
              sidebarRef={sidebarRef}
              logo={logo}
              mapDisplayName={communityMapData.name}
              gameName={gameData?.name || ''}
              voteStatus={voteStatus}
              handleVote={handleVote}
              user={user}
              isFavourited={isFavourited}
              favLoading={favLoading}
              handleFavourite={handleFavourite}
              customCategories={customCategories}
              customTypes={customTypes}
              typeColours={typeColours}
              visibleTypes={visibleTypes}
              visibleCategories={visibleCategories}
              hoveredType={hoveredType}
              setHoveredType={() => {}}
              handleTypeHover={(type) => setHoveredTypeSmooth(type)}
              handleTypeUnhover={() => setHoveredTypeSmooth(null)}
              hoveredPoiId={hoveredPoiId}
              setHoveredPoiId={(id) => setHoveredPoiIdSmooth(id)}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              expandedTypes={expandedTypes}
              setExpandedTypes={setExpandedTypes}
              toggleAllTypes={toggleAllTypes}
              allTypesVisible={allTypesVisible}
              toggleCategoryExpand={toggleCategoryExpand}
              toggleCategory={toggleCategory}
              toggleType={toggleType}
              handleMouseDown={() => {}}
              pois={pois}
              tipUrl={communityMapData.tip_url}
              poiLabelToggleBtn={poiLabelToggleBtn}
              flashType={flashType}
              setFlashType={setFlashType}
              onMobileTypeClick={handleMobileTypeClick}
              isMobile={true}
              onClose={() => setMobileDrawer(null)}
              zones={zones}
              visibleZones={visibleZones}
              setVisibleZones={setVisibleZones}
              hoveredZoneId={hoveredZoneId}
              setHoveredZoneId={setHoveredZoneId}
              hideSidebarHover={hideSidebarHover}
              setHideSidebarHover={setHideSidebarHover}
              extraRegionMaps={extraRegionMaps}
              isMapOwner={isMapOwnerOrFounder}
              onAddNewRegionMap={handleAddNewRegionMap}
              onRegionMapClick={handleRegionMapClick}
              parentMapId={communityMapData.id}
              styles={mapStyles}
              stylesLoading={stylesLoading}
              stylesError={stylesError}
              mapId={communityMapId}
              pathControls={pathControls}
              onPathControlChange={handlePathControlChange}
              activeOverlayId={activeOverlayId}
              hasActiveOverlay={hasActiveOverlay}
              isPublic={isPublic ? 1 : 0}
              seoApproved={seoApproved ? 1 : 0}
              allowAds={mapData?.allow_ads}
              isEmbed={false}
              onUserLogin={(u) => setUser(u)}
              rememberMapPrefs={rememberResolved ? rememberMapPrefs : true}
            />
          )}

          {mobileDrawer === 'comments' && (
            <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
              <Comments mapId={communityMapId} user={user} mapOwnerId={communityMapData?.uploader_id} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMobileTabBar = () => (
    <div id="mobile-tab-bar" className="mobile-tab-bar">
      <button
        className={`mobile-tab ${mobileDrawer === 'sidebar' ? 'active' : ''}`}
        onClick={() => setMobileDrawer('sidebar')}
      >
        <span style={{ fontSize: 26, display: 'block' }}>☰</span>
      </button>
      <button
        className={`mobile-tab ${mobileDrawer === 'comments' ? 'active' : ''}`}
        onClick={() => setMobileDrawer('comments')}
      >
        <span style={{ fontSize: 24, display: 'block' }}>💬</span>
      </button>

      {(hasOverlayChoices || hasActiveOverlay) && (
        <button
          className={`mobile-tab ${mobileDrawer === 'overlay' ? 'active' : ''}`}
          onClick={() => {
            const next = mobileDrawer === 'overlay' ? null : 'overlay';
            setMobileDrawer(next);
            if (next) loadOverlayAssetsForMap();
          }}
        >
          <span style={{ fontSize: 24, display: 'block' }}>▦</span>
        </button>
      )}
    </div>
  );

  const mainBgColour = mapStyles?.main_bg_colour || '#1a1c1e';

  useEffect(() => {
    document.documentElement.style.setProperty('--main-bg-colour', mainBgColour);
  }, [mainBgColour]);

  const effectiveVisibleZoneIds = useMemo(() => {
    if (!Array.isArray(zones) || zones.length === 0) {
      return visibleZones;
    }

    const result = new Set();

    zones.forEach((zone) => {
      const id = zone.id;
      if (!visibleZones.has(id)) return;

      let overlayId =
        zone.overlay_id != null
          ? zone.overlay_id
          : zone.overlayId != null
          ? zone.overlayId
          : null;

      if (overlayId === '' || overlayId === undefined) {
        overlayId = null;
      }

      if (overlayId === null) {
        result.add(id);
        return;
      }

      const numericOverlayId = Number(overlayId);
      if (!Number.isFinite(numericOverlayId)) {
        result.add(id);
        return;
      }

      if (numericOverlayId === 1) {
        if (!hasActiveOverlay) {
          result.add(id);
        }
        return;
      }

      if (!hasActiveOverlay) {
        return;
      }

      if (activeOverlayId != null && String(numericOverlayId) === String(activeOverlayId)) {
        result.add(id);
      }
    });

    return result;
  }, [zones, visibleZones, activeOverlayId, hasActiveOverlay]);

  if (status === 'loading') {
    return (
      <div style={{ padding: '2rem', color: 'white', textAlign: 'center' }}>
        Map loading…
      </div>
    );
  }
  if (status === 'notfound') {
    return (
      <div style={{ padding: '2rem', color: 'white', textAlign: 'center' }}>
        Map not found
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ padding: '2rem', color: 'white', textAlign: 'center' }}>
        Error loading map{errorMsg ? `: ${errorMsg}` : ''}
      </div>
    );
  }

  if (!mapData || !communityMapData || (!imageSize && !mapData.tile_url)) {
    return (
      <div style={{ padding: '2rem', color: 'white', textAlign: 'center' }}>
        Map loading…
      </div>
    );
  }

  const mapDisplayName = mapData.name || '';
  const gameName = gameData?.name || '';

  const pageTitle = (() => {
    if (gameName && mapDisplayName) return `${gameName} Interactive Map - ${mapDisplayName} | Point Atlas`;
    if (mapDisplayName) return `${mapDisplayName} Interactive Map | Point Atlas`;
    if (gameName) return `${gameName} Interactive Map | Point Atlas`;
    return 'Point Atlas';
  })();

  const baseDescription = (() => {
    if (mapDisplayName) return `Explore the ${mapDisplayName} interactive map on Point Atlas.`;
    if (gameName) return `Explore the ${gameName} interactive map on Point Atlas.`;
    return 'Explore interactive game maps on Point Atlas.';
  })();

  const pageDescription = seoExtra ? `${baseDescription} ${seoExtra}` : baseDescription;

  const baseKeywordsParts = [];
  if (mapDisplayName) {
    baseKeywordsParts.push(mapDisplayName, `${mapDisplayName} map`, `${mapDisplayName} interactive map`);
  }
  if (gameName) {
    baseKeywordsParts.push(gameName, `${gameName} map`, `${gameName} interactive map`);
  }
  baseKeywordsParts.push('interactive map', 'game maps', 'Point Atlas');
  const baseKeywords = baseKeywordsParts.join(', ');
  const pageKeywords = seoExtra ? `${baseKeywords}, ${seoExtra}` : baseKeywords;

  const sidebarTabOffset = sidebarOpen ? sidebarWidth + 36 : 36;
  const commentsTabOffset = commentsOpen ? commentsWidth + 36 : 36;

  const effectivePageIsPublic = !!mapData.is_public;

  const isSeoIndexable = effectivePageIsPublic && seoApproved;
  const robotsMetaContent = isLegacyUrl ? 'noindex,follow' : (isSeoIndexable ? null : 'noindex');

  const canonicalSlug = slugifyName(mapDisplayName || gameName || 'Map');
  const isVttRouteCanonical =
    (location && typeof location.pathname === 'string' && location.pathname.startsWith('/vtt/')) || false;
  const canonicalBasePath = isVttRouteCanonical ? '/vtt' : '/map';
  const canonicalPath = `${canonicalBasePath}/${encodeURIComponent(String(mapData.id))}/${encodeURIComponent(canonicalSlug)}`;

  const canonicalOrigin = (() => {
    if (IS_DEV) return 'https://beta.point-atlas.io';
    if (typeof window !== 'undefined' && window.location && window.location.origin) return window.location.origin;
    return 'https://point-atlas.io';
  })();

  const canonicalUrl = `${canonicalOrigin}${canonicalPath}`;

  const ArrowIcon = ({ direction = 'left' }) => (
    <svg width="23" height="23" viewBox="0 0 23 23" style={{ display: 'block' }}>
      {direction === 'left' ? (
        <polyline
          points="15,6 9,11.5 15,17"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <polyline
          points="8,6 14,11.5 8,17"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:url" content={canonicalUrl} />
        {robotsMetaContent && <meta name="robots" content={robotsMetaContent} />}
      </Helmet>

      <div
        ref={mainMapRef}
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'row',
          background: mainBgColour,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {!isMobile && (
          <>
            <div style={{ display: 'flex', flexDirection: 'row', zIndex: 110, position: 'relative' }}>
              {sidebarOpen && (
                <Sidebar
                  key={communityMapData?.id}
                  sidebarWidth={sidebarWidth}
                  sidebarRef={sidebarRef}
                  logo={logo}
                  mapDisplayName={communityMapData.name}
                  gameName={gameName}
                  voteStatus={voteStatus}
                  handleVote={handleVote}
                  user={user}
                  isFavourited={isFavourited}
                  favLoading={favLoading}
                  handleFavourite={handleFavourite}
                  customCategories={customCategories}
                  customTypes={customTypes}
                  typeColours={typeColours}
                  visibleTypes={visibleTypes}
                  visibleCategories={visibleCategories}
                  hoveredType={hoveredType}
                  setHoveredType={() => {}}
                  handleTypeHover={(type) => setHoveredTypeSmooth(type)}
                  handleTypeUnhover={() => setHoveredTypeSmooth(null)}
                  hoveredPoiId={hoveredPoiId}
                  setHoveredPoiId={(id) => setHoveredPoiIdSmooth(id)}
                  expandedCategories={expandedCategories}
                  setExpandedCategories={setExpandedCategories}
                  expandedTypes={expandedTypes}
                  setExpandedTypes={setExpandedTypes}
                  toggleAllTypes={toggleAllTypes}
                  allTypesVisible={allTypesVisible}
                  toggleCategoryExpand={toggleCategoryExpand}
                  toggleCategory={toggleCategory}
                  toggleType={toggleType}
                  handleMouseDown={handleSidebarMouseDown}
                  pois={pois}
                  tipUrl={communityMapData.tip_url}
                  poiLabelToggleBtn={poiLabelToggleBtn}
                  flashType={flashType}
                  setFlashType={setFlashType}
                  zones={zones}
                  visibleZones={visibleZones}
                  setVisibleZones={setVisibleZones}
                  hoveredZoneId={hoveredZoneId}
                  setHoveredZoneId={setHoveredZoneId}
                  hideSidebarHover={hideSidebarHover}
                  setHideSidebarHover={setHideSidebarHover}
                  extraRegionMaps={extraRegionMaps}
                  isMapOwner={isMapOwnerOrFounder}
                  onAddNewRegionMap={handleAddNewRegionMap}
                  onRegionMapClick={handleRegionMapClick}
                  parentMapId={communityMapData.id}
                  styles={mapStyles}
                  stylesLoading={stylesLoading}
                  stylesError={stylesError}
                  mapId={communityMapId}
                  pathControls={pathControls}
                  onPathControlChange={handlePathControlChange}
                  activeOverlayId={activeOverlayId}
                  hasActiveOverlay={hasActiveOverlay}
                  isPublic={isPublic ? 1 : 0}
                  seoApproved={seoApproved ? 1 : 0}
                  allowAds={mapData?.allow_ads}
                  isEmbed={false}
                  onUserLogin={(u) => setUser(u)}
                  rememberMapPrefs={rememberResolved ? rememberMapPrefs : true}
                />
              )}
            </div>

            <button
              onClick={() => setSidebarOpen(v => !v)}
              style={{
                position: 'absolute',
                left: sidebarOpen ? (sidebarWidth - -40) : 0,
                top: 100,
                zIndex: 1011,
                width: 36,
                height: 48,
                background: '#232336',
                color: '#fff',
                border: 'none',
                borderRadius: '0 14px 14px 0',
                boxShadow: '0 2px 12px #0007',
                fontWeight: 800,
                fontSize: 22,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'left 0.23s cubic-bezier(.44,1.44,.4,1.04)'
              }}
              aria-label={sidebarOpen ? 'Hide Map Info' : 'Show Map Info'}
            >
              <ArrowIcon direction={sidebarOpen ? 'left' : 'right'} />
            </button>

            <button
              onClick={() => setCommentsOpen(v => !v)}
              style={{
                position: 'absolute',
                right: commentsOpen ? commentsWidth : 0,
                top: 160,
                zIndex: 1011,
                width: 36,
                height: 48,
                background: '#232336',
                color: '#fff',
                border: 'none',
                borderRadius: '14px 0 0 14px',
                boxShadow: '0 2px 12px #0007',
                fontWeight: 900,
                fontSize: 20,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'right 0.23s cubic-bezier(.44,1.44,.4,1.04)'
              }}
              aria-label={commentsOpen ? 'Hide Comments' : 'Show Comments'}
              title={commentsOpen ? 'Hide Comments' : 'Show Comments'}
            >
              {commentsOpen ? <ArrowIcon direction="right" /> : <span style={{ lineHeight: 1 }}>💬</span>}
            </button>
          </>
        )}

        <div
          style={{
            flex: '1 1 0%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {isMapOwnerOrFounder && !isMobile && (
            <MapPrivacyControls
              mapId={mapData.id}
              user={user}
              isOwner={!!user && user.id === mapData?.uploader_id}
              isFounder={!!user && user.id === 1}
              isPublic={isPublic}
              setIsPublic={setIsPublic}
              publishing={publishing}
              setPublishing={setPublishing}
              pubStatusMsg={pubStatusMsg}
              setPubStatusMsg={setPubStatusMsg}
              seoApproved={seoApproved}
              setSeoApproved={setSeoApproved}
              seoUpdating={seoUpdating}
              setSeoUpdating={setSeoUpdating}
              seoStatusMsg={seoStatusMsg}
              setSeoStatusMsg={setSeoStatusMsg}
              isSeoIndexable={isSeoIndexable}
              rightOffset={18}
              onRefetchCommunityMap={refetchCurrentMapState}
            />
          )}

          <InteractiveMap
            key={mapKey}
            leafletMap={leafletMap}
            setLeafletMap={setLeafletMap}
            mapData={mapData}
            imageSize={imageSize}
            pois={pois}
            foundPOIs={foundPOIs}
            showFound={showFound}
            setShowFound={setShowFound}
            visibleTypes={visibleTypes}
            visibleCategories={visibleCategories}
            hoveredType={hoveredType}
            hoveredPoiId={hoveredPoiId}
            setHoveredPoiId={setHoveredPoiIdSmooth}
            pendingPOI={pendingPOI}
            setPendingPOI={setPendingPOI}
            isMapOwnerOrFounder={isMapOwnerOrFounder}
            handleMapCoordClick={handleMapCoordClick}
            handleClick={handleClick}
            mouseCoord={mouseCoord}
            handleMouseCoord={handleMouseCoord}
            shareCoord={shareCoord}
            handleCopyCoord={handleCopyCoord}
            copiedCoord={copiedCoord}
            customCategories={customCategories}
            typeColours={typeColours}
            customTypes={customTypes}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            typeColourInput={typeColourInput}
            setTypeColourInput={setTypeColourInput}
            shape={shape}
            setShape={setShape}
            fill={fill}
            opacity={opacity}
            pattern={pattern}
            description={description}
            setDescription={setDescription}
            savePOI={(poiNameArg, imageUrlArg, descriptionArg, overlayIdArg, localTypeColourArg, createStylePayloadArg) => {
              let styleObj = {};
              let overlayIdToSend;

              if (overlayIdArg && typeof overlayIdArg === 'object' && !Array.isArray(overlayIdArg)) {
                styleObj = overlayIdArg;
                overlayIdToSend = Object.prototype.hasOwnProperty.call(overlayIdArg, 'overlay_id')
                  ? overlayIdArg.overlay_id
                  : undefined;
              } else {
                overlayIdToSend = overlayIdArg === undefined ? undefined : overlayIdArg;
                if (createStylePayloadArg && typeof createStylePayloadArg === 'object' && !Array.isArray(createStylePayloadArg)) {
                  styleObj = createStylePayloadArg;
                }
              }

              return savePOI(poiNameArg, imageUrlArg, descriptionArg, {
                ...styleObj,
                ...(overlayIdToSend !== undefined ? { overlay_id: overlayIdToSend } : {})
              });
            }}
            showAllPoiLabels={showAllPoiLabels}
            setShowAllPoiLabels={setShowAllPoiLabels}
            onToggleFound={togglePOIFound}
            onCategoryUpdate={handleCategorySaved}
            user={user}
            mapOwnerId={communityMapData.uploader_id}
            commentsOpen={commentsOpen}
            commentsWidth={commentsWidth}
            poiName={poiName}
            setPoiName={setPoiName}
            flashType={flashType}
            activeTool={activeTool}
            pencilSize={pencilSize}
            pendingLayer={pendingLayer}
            visibleZoneIds={effectiveVisibleZoneIds}
            onZonesChanged={handleZonesChanged}
            hideSidebarHover={hideSidebarHover}
            setHideSidebarHover={setHideSidebarHover}
            hoveredZoneId={hoveredZoneId}
            sidebarTabOffset={sidebarTabOffset}
            styles={mapStyles}
            stylesLoading={stylesLoading}
            stylesError={stylesError}
            pathControls={pathControls}
            extraRegionMaps={extraRegionMaps}
            parentMapId={parentMapData ? parentMapData.id : (mapData.parent_map_id || null)}
            parentMapName={parentMapData?.name ?? communityMapData?.name ?? 'Parent Map'}
            currentMapId={mapData.id}
            onSeoEditClick={handleSeoEditClick}
            onOpenPrivacyCookieSettings={openPrivacyCookieSettings}
            onOverlayStateChange={({ activeOverlayId, hasActiveOverlay }) => {
              setActiveOverlayId(activeOverlayId);
              setHasActiveOverlay(hasActiveOverlay);
            }}
          />

          {showVoxPromo && (
            <div
              style={{
                position: 'absolute',
                left: isMobile ? 12 : 18,
                bottom: isMobile ? 76 : 18,
                zIndex: 1305,
                maxWidth: isMobile ? 'calc(100vw - 24px)' : 420,
                background: 'rgba(13,16,24,0.94)',
                border: '1px solid rgba(86,98,145,0.9)',
                borderRadius: 14,
                boxShadow: '0 12px 34px rgba(0,0,0,0.45)',
                color: '#f4f7ff',
                padding: isMobile ? '10px 12px 10px 12px' : '12px 14px 12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}
            >
              <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.2, opacity: 0.96 }}>
                  Built something new: Voxemera
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.35, opacity: 0.82, marginTop: 2 }}>
                  Live voice and video. Built independently.
                </div>
              </div>

              <a
                href="https://voxemera.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismissVoxPromo}
                style={{
                  flexShrink: 0,
                  textDecoration: 'none',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: '#20263a',
                  border: '1px solid rgba(99,114,170,0.95)',
                  color: '#f4f7ff',
                  fontSize: 12,
                  fontWeight: 800,
                  whiteSpace: 'nowrap'
                }}
              >
                Check it out
              </a>

              <button
                type="button"
                onClick={dismissVoxPromo}
                aria-label="Dismiss Voxemera notice"
                style={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: '1px solid rgba(86,98,145,0.85)',
                  background: 'rgba(255,255,255,0.03)',
                  color: '#d8def8',
                  fontSize: 16,
                  lineHeight: 1,
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>
          )}

          <div
            style={{
              position: 'absolute',
              top: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1200,
              background: '#222d',
              color: '#ffe670',
              fontWeight: 700,
              borderRadius: 7,
              padding: '6px 17px 6px 13px',
              fontFamily: 'monospace',
              fontSize: 15,
              pointerEvents: 'none',
              boxShadow: '0 1px 6px #0005',
              letterSpacing: 1,
              display: mouseCoord ? 'block' : 'none'
            }}
          >
            {mouseCoord && (
              <>
                X: {Math.round(mouseCoord.x)} <span style={{ margin: '0 8px' }} /> Y: {Math.round(mouseCoord.y)}
              </>
            )}
          </div>
        </div>

        {!isMobile && commentsOpen && (
          <div
            ref={commentsPanelRef}
            style={{
              width: commentsWidth,
              maxWidth: '80vw',
              minWidth: 260,
              height: '100%',
              background: '#101318',
              borderLeft: '1px solid rgba(70,80,120,0.9)',
              position: 'relative',
              zIndex: 120,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div
              onMouseDown={handleCommentsMouseDown}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: 'col-resize',
                zIndex: 1300
              }}
              title="Resize comments"
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderBottom: '1px solid rgba(70,80,120,0.7)',
                background: 'rgba(20,24,34,0.95)',
                color: '#fff'
              }}
            >
              <div style={{ fontWeight: 900, letterSpacing: 0.2 }}>💬 Comments</div>
              <button
                type="button"
                onClick={() => setCommentsOpen(false)}
                style={{
                  marginLeft: 'auto',
                  padding: '6px 10px',
                  borderRadius: 10,
                  border: '1px solid rgba(80,90,130,0.9)',
                  background: '#1b1f2e',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 900,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            <div style={{ flex: '1 1 0%', overflow: 'auto' }}>
              <Comments mapId={communityMapId} user={user} mapOwnerId={communityMapData?.uploader_id} />
            </div>
          </div>
        )}

        {isMobile && renderMobileTabBar()}
        {isMobile && renderMobileDrawer()}

        <SaveLayerModal
          open={showSaveLayerModal}
          onSave={({ name, isPublic }) => {
            setPendingLayer({ name, isPublic });
            setShowSaveLayerModal(false);
          }}
          onCancel={() => setShowSaveLayerModal(false)}
        />

        {isMapOwnerOrFounder && stylerOpen && (
          <Styler
            mapId={mapId}
            user={user}
            open={stylerOpen}
            onClose={() => {
              setStylerOpen(false);
              refetchStyles();
            }}
          />
        )}
      </div>
    </>
  );
}

export default MapView;